/**
 * Vuex module for managing the configuration of the Finio form.
 *
 * This module handles the state, mutations, and actions related to the configuration
 * of the Finio form, including the form type, API version, and various feature flags.
 *
 * The module also provides getters to retrieve the current form configuration and steps.
 */

import { forms } from "@/formConfigurations";

export default {
  namespaced: true,

  /**
   * The state object for the configuration Vuex module.
   *
   * This state object contains various configuration properties for the Finio form, including:
   * - `forms`: An object containing form configurations from the `./formConfigurations.js` file.
   * - `formType`: The current form type.
   * - `tpspName`: The name of the TPSP (Third Party Service Provider).
   * - `apiVersion`: The API version to use.
   * - `showAdditionalInformation`: A flag to control the display of additional information section (UnavailablePaltforms.vue).
   * - `showTpspInternalId`: A flag to control the display of the TPSP internal ID field.
   * - `platformRequestEmailAdditionalCc`: The email address for additional CC on platform requests.
   * - `finioProductValuations`: A flag to control the display of the Finio product valuations.
   * - `finioProductTransactions`: A flag to control the display of the Finio product transactions.
   * - `finioProductHistory`: A flag to control the display of the Finio product history.
   * - `finioProductIncomeStatements`: A flag to control the display of the Finio product income statements.
   */
  state: {
    // Form Type definitions from ./formConfigurations.js
    forms: forms,

    // Config Items from Main Props.
    formType: "",
    tpspName: "",
    apiVersion: "",
    showAdditionalInformation: false,
    showTpspInternalId: false,
    tpspIdLabel: "",
    platformRequestEmailAdditionalCc: "",
    finioProductValuations: false,
    finioProductTransactions: false,
    finioProductHistory: false,
    finioProductIncomeStatements: false,
    redirectUrl: null,
    platformGuideLabel: "View the Platform Guide",
    hasUrlPrefill: false,
  },

  /**
   * Mutations for updating the configuration state in the Vuex store.
   *
   * The `SET_CONFIG` mutation updates the configuration state with the provided `config` object.
   * It iterates over the keys in the `config` object and updates the corresponding keys in the state,
   * if they exist. If the `config` object is invalid, a warning is logged.
   *
   * The `SET_FORM_TYPE` mutation updates the `formType` property in the state with the provided `formType`.
   */
  mutations: {
    SET_CONFIG(state, config) {
      if (config && typeof config === "object") {
        Object.keys(config).forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(state, key)) {
            state[key] = config[key];
          }
        });
      } else {
        console.warn("Invalid config object passed to SET_CONFIG mutation");
      }
    },
    SET_FORM_TYPE(state, formType) {
      state.formType = formType;
    },
  },

  /**
   * Initializes the configuration state in the Vuex store.
   *
   * This action is responsible for committing the provided `config` object to the `SET_CONFIG` mutation,
   * and also committing the `formType` property from the `config` object to the `SET_FORM_TYPE` mutation.
   *
   * @param {Object} config - The configuration object to be set in the store.
   * @param {string} [config.formType=tpsp] - The form type to be set in the store.
   */
  actions: {
    initializeConfig({ commit }, config) {
      commit("SET_CONFIG", config);
      commit("SET_FORM_TYPE", config.formType || "tpsp");
    },
  },

  /**
   * Getters for accessing the configuration state in the Vuex store.
   *
   * The `getConfig` getter returns the entire configuration state.
   *
   * The `getCurrentFormConfig` getter returns the configuration for the current form type, if it exists.
   * If the form configuration is not found, a warning is logged and an empty object is returned.
   *
   * The `getCurrentFormSteps` getter returns the steps for the current form type, if they exist.
   * If the steps are not found, a warning is logged and an empty array is returned.
   */
  getters: {
    getConfig: (state) => state,
    getCurrentFormConfig: (state) => {
      if (state.formType && state.forms[state.formType]) {
        return state.forms[state.formType];
      } else {
        console.warn(
          `Form configuration not found for formType: ${state.formType}`
        );
        return {}; // or handle fallback logic as per your application's needs
      }
    },
    getCurrentFormSteps: (state) => {
      if (
        state.formType &&
        state.forms[state.formType] &&
        state.forms[state.formType].steps
      ) {
        return state.forms[state.formType].steps;
      } else {
        console.warn(`Steps not found for formType: ${state.formType}`);
        return []; // Return an empty array if steps are not found
      }
    },
    getTpspName: (state) => state.tpspName,
  },
};
